import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, RefreshCw, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function DiagnosticaPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/system/routes-status');
      setStatus(data);
    } catch (e) {
      toast.error('Impossibile contattare /api/system/routes-status: ' + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (!status && loading) {
    return <div className="p-8 flex items-center gap-2 text-slate-600"><Loader2 className="w-4 h-4 animate-spin" /> Lettura stato sistema…</div>;
  }

  return (
    <div className="space-y-4 max-w-4xl mx-auto p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="diagnostica-title">Diagnostica Sistema</h1>
          <p className="text-sm text-slate-600">Stato dei moduli e delle integrazioni esterne</p>
        </div>
        <Button onClick={load} disabled={loading} data-testid="diagnostica-reload">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {status && (
        <>
          {/* Sommario */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Sommario</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-green-50 border border-green-200 rounded p-3" data-testid="loaded-count">
                  <div className="text-2xl font-bold text-green-700">{status.loaded_count}</div>
                  <div className="text-xs text-green-900">Moduli caricati</div>
                </div>
                <div className={`${status.failed_count > 0 ? 'bg-red-50 border-red-300' : 'bg-slate-50 border-slate-200'} border rounded p-3`} data-testid="failed-count">
                  <div className={`text-2xl font-bold ${status.failed_count > 0 ? 'text-red-700' : 'text-slate-500'}`}>{status.failed_count}</div>
                  <div className="text-xs">Moduli FALLITI</div>
                </div>
                <div className={`${status.emergent_llm_key_present ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-300'} border rounded p-3`}>
                  <div className="flex items-center gap-2">
                    {status.emergent_llm_key_present ? <CheckCircle2 className="w-5 h-5 text-green-700" /> : <XCircle className="w-5 h-5 text-red-700" />}
                    <span className="text-xs font-medium">EMERGENT_LLM_KEY</span>
                  </div>
                  <div className="text-xs mt-1 text-slate-600">{status.emergent_llm_key_present ? 'Presente' : 'MANCANTE — aggiungi in .env'}</div>
                </div>
                <div className={`${status.mongo_url_present ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-300'} border rounded p-3`}>
                  <div className="flex items-center gap-2">
                    {status.mongo_url_present ? <CheckCircle2 className="w-5 h-5 text-green-700" /> : <XCircle className="w-5 h-5 text-red-700" />}
                    <span className="text-xs font-medium">MONGO_URL</span>
                  </div>
                  <div className="text-xs mt-1 text-slate-600">{status.mongo_url_present ? 'Presente' : 'MANCANTE'}</div>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-3">Python: <code>{status.python_version}</code></div>
            </CardContent>
          </Card>

          {/* Router list */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                Moduli backend
                {status.failed_count > 0 && <Badge className="bg-red-600">⚠ {status.failed_count} falliti</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="routers-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left">
                    <th className="p-2 text-xs">Modulo</th>
                    <th className="p-2 text-xs">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(status.routers || {}).map(([name, st]) => {
                    const ok = st === 'loaded';
                    return (
                      <tr key={name} className={`border-b ${ok ? '' : 'bg-red-50'}`}>
                        <td className="p-2 font-mono text-xs">{name}</td>
                        <td className="p-2">
                          {ok ? (
                            <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                              <CheckCircle2 className="w-3 h-3" /> caricato
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-700 text-xs">
                              <AlertCircle className="w-3 h-3" /> <code className="text-[11px]">{st}</code>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {status.failed_count > 0 && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="pt-4 text-sm space-y-2">
                <p className="font-semibold">⚠ Hai {status.failed_count} moduli falliti sulla VPS. Probabili cause:</p>
                <ul className="list-disc ml-5 text-xs space-y-1">
                  <li><strong>ModuleNotFoundError: emergentintegrations</strong> → SSH sulla VPS ed esegui: <code className="bg-white px-1 rounded">pip install emergentintegrations --extra-index-url https://d33sy5i8bnduwe.cloudfront.net/simple/</code></li>
                  <li><strong>ModuleNotFoundError: playwright</strong> → <code className="bg-white px-1 rounded">pip install playwright && playwright install chromium</code></li>
                  <li><strong>ImportError nei file routes/</strong> → riesegui <code className="bg-white px-1 rounded">recovery_v99.sh</code> per riallineare /backend/routes/</li>
                </ul>
                <p className="text-xs">Poi: <code className="bg-white px-1 rounded">systemctl restart cen-backend</code> e ricarica questa pagina.</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
